/** ローカル → OneDrive の反映（仕様書 §7.3）。 */

import { getDb } from '../db/schema';
import type { OutboxOp } from '../db/schema';
import { graph } from '../auth/GraphClient';
import { getDeviceId } from '../domain/ids';
import { nowIsoUtc } from '../lib/datetime';
import type {
  Category,
  Entry,
  GoalsDoc,
  MonthKey,
  Project,
  ProjectsDoc,
  SettingsDoc,
  TaskDef,
  TasksDoc,
} from '../domain/types';
import { SCHEMA_VERSION } from '../domain/types';
import { APP_VERSION } from '../version';
import {
  CATEGORIES_PATH,
  conflictPath,
  dailyPath,
  entryPath,
  GOALS_PATH,
  indexPath,
  mediaPath,
  PROJECTS_PATH,
  reviewPath,
  SETTINGS_PATH,
  strokesPath,
  taskDailyPath,
  TASKS_PATH,
  taskWeeklyPath,
  tombstonePath,
} from './paths';
import {
  buildIndex,
  pickWinner,
  toCloudEntry,
  toConflictDoc,
  type TombstoneDoc,
} from './serialize';

/** 影響を受けた月。処理の最後にまとめて index.json を作り直す。 */
type Touched = Set<MonthKey>;

/** index.json はローカルの記事と一日のまとめから作り直せる派生キャッシュ（§4.3）。 */
async function rebuildIndex(month: MonthKey): Promise<void> {
  const db = await getDb();
  const entries = await db.getAllFromIndex('entries', 'by_month', month);
  const dailies = await db.getAllFromIndex('dailies', 'by_month', month);
  const taskEntries = await db.getAllFromIndex('taskEntries', 'by_month', month);
  const doc = buildIndex(month, entries, dailies, taskEntries, getDeviceId(), nowIsoUtc());

  if (entries.length === 0 && dailies.length === 0 && taskEntries.length === 0) {
    // その月の記事が全部消えたらインデックスも消す
    await graph.delete(indexPath(month)).catch(() => undefined);
    return;
  }
  // 競合しても内容はローカルから再生成できるので、If-Match は使わず上書きする
  await graph.putJson(indexPath(month), doc);
}

async function uploadMedia(mediaId: string): Promise<void> {
  const db = await getDb();
  const rec = await db.get('media', mediaId);
  if (!rec) return; // すでに削除済み
  if (rec._uploaded) return;

  // 深いパスへのPUTは親フォルダを自動生成する（フォルダを明示的に作ると400になる）
  const put = await graph.put(mediaPath(rec.month, rec.file), rec.blob, rec.mime);
  if (put === 'conflict') return;

  const stroke = await db.get('strokes', mediaId);
  if (stroke) {
    await graph.putJson(strokesPath(rec.month, `${mediaId}.strokes.json`), stroke.data);
    await db.put('strokes', { ...stroke, month: rec.month, _uploaded: true });
  }

  const fresh = await db.get('media', mediaId);
  if (fresh) await db.put('media', { ...fresh, _uploaded: true });
}

/**
 * 記事の月が変わったとき、メディアのレコードを新しい月に合わせ、再アップロード対象にする。
 * ローカルにBlobが無い（LRUで破棄された）場合は、旧パスから取り直してから移す。
 */
async function realignMedia(entryId: string, month: MonthKey, prevMonth?: MonthKey): Promise<void> {
  const db = await getDb();
  const items = await db.getAllFromIndex('media', 'by_entry', entryId);
  for (const m of items) {
    if (m.month === month) continue;
    await db.put('media', { ...m, month, _uploaded: false });
    const stroke = await db.get('strokes', m.id);
    if (stroke) await db.put('strokes', { ...stroke, month, _uploaded: false });
  }

  if (!prevMonth) return;
  // Blobが手元に無いものは旧パスから回収する
  for (const m of items) {
    if (m.blob) continue;
    const blob = await graph.getBlob(mediaPath(prevMonth, m.file)).catch(() => null);
    if (blob) await db.put('media', { ...m, month, blob, bytes: blob.size, _uploaded: false });
  }
}

/** 記事の保存。412 は競合として処理する。 */
async function pushEntry(op: OutboxOp, touched: Touched): Promise<void> {
  const db = await getDb();
  const rec = await db.get('entries', op.entryId!);
  if (!rec) return; // 保存後に削除された

  // 月が変わっていればメディアの所属も揃えてから上げる（§7.6）
  await realignMedia(rec.id, rec.monthKey, op.prevMonth);

  // JSONが参照する画像を先に上げる（§7.2）
  const mediaIds = await db.getAllKeysFromIndex('media', 'by_entry', rec.id);
  for (const id of mediaIds) await uploadMedia(String(id));

  const month = rec.monthKey;
  const path = entryPath(month, rec.id);
  const cloud = toCloudEntry(rec);
  let result = await graph.putJson(path, cloud, rec._etag);

  if (result === 'conflict') {
    const remote = await graph.getJson<Entry>(path);
    if (remote) {
      const { winner, loser } = pickWinner(cloud, remote.data);
      await graph.putJson(
        conflictPath(rec.id, loser.deviceId, nowIsoUtc()),
        toConflictDoc(loser, winner, nowIsoUtc()),
      );
      if (winner === cloud) {
        result = await graph.putJson(path, cloud, remote.eTag);
      } else {
        // リモートが勝った。ローカルを置き換える
        const { putFromRemote } = await import('../db/entries');
        await putFromRemote(remote.data, remote.eTag);
        touched.add(month);
        await bumpConflictCount();
        return;
      }
      await bumpConflictCount();
    } else {
      result = await graph.putJson(path, cloud);
    }
  }

  if (result !== 'conflict') {
    const fresh = await db.get('entries', rec.id);
    if (fresh) await db.put('entries', { ...fresh, _dirty: false, _etag: result.eTag });
  }
  touched.add(month);

  // 日付変更で月をまたいだ場合、旧月のファイルを片付ける（§7.6）。
  // 新しい月へは realignMedia + uploadMedia で既に上げ直してある。
  if (op.prevMonth && op.prevMonth !== month) {
    await graph.delete(entryPath(op.prevMonth, rec.id)).catch(() => undefined);
    for (const m of rec.media) {
      await graph.delete(mediaPath(op.prevMonth, m.file)).catch(() => undefined);
      if (m.strokesFile) {
        await graph.delete(strokesPath(op.prevMonth, m.strokesFile)).catch(() => undefined);
      }
    }
    touched.add(op.prevMonth);
  }
}

async function pushDelete(op: OutboxOp, touched: Touched): Promise<void> {
  const db = await getDb();
  const id = op.entryId!;
  const month = op.month!;
  const tomb = await db.get('tombstones', id);

  await graph.delete(entryPath(month, id)).catch(() => undefined);

  // 墓標を追記する（他端末での復活を防ぐ・§5.5）
  const deletedAt = tomb?.deletedAt ?? nowIsoUtc();
  const tombMonth = deletedAt.slice(0, 7);
  const path = tombstonePath(tombMonth);
  const existing = await graph.getJson<TombstoneDoc>(path);
  const doc: TombstoneDoc = existing?.data ?? {
    schemaVersion: SCHEMA_VERSION,
    month: tombMonth,
    items: [],
  };
  if (!doc.items.some((i) => i.entryId === id)) {
    doc.items.push({
      entryId: id,
      entryMonth: tomb?.entryMonth ?? month,
      deletedAt,
      deviceId: tomb?.deviceId ?? getDeviceId(),
    });
  }
  await graph.putJson(path, doc, existing?.eTag ?? null);

  touched.add(month);
}

async function pushMediaDelete(op: OutboxOp): Promise<void> {
  const month = op.month!;
  const id = op.targetKey.replace(/^media:/, '');
  // ファイル名は id を含むため、拡張子違いの両方を試す
  for (const file of [`${id}.jpg`, `${id}.png`]) {
    await graph.delete(mediaPath(month, file)).catch(() => undefined);
  }
  await graph.delete(strokesPath(month, `${id}.strokes.json`)).catch(() => undefined);
}

/** 配列は id 単位で統合し、同一 id は updatedAt の新しい方を採る（§7.3）。 */
async function pushCategories(): Promise<void> {
  const db = await getDb();
  const local = await db.getAll('categories');
  const remote = await graph.getJson<{ categories: Category[]; updatedAt: string }>(CATEGORIES_PATH);

  const merged = new Map<string, Category>();
  for (const c of remote?.data.categories ?? []) merged.set(c.id, c);
  for (const c of local) merged.set(c.id, c); // ローカルの編集を優先

  await graph.putJson(
    CATEGORIES_PATH,
    {
      schemaVersion: SCHEMA_VERSION,
      updatedAt: nowIsoUtc(),
      categories: [...merged.values()].sort((a, b) => a.order - b.order),
    },
    remote?.eTag ?? null,
  );
}

async function pushSettings(): Promise<void> {
  const db = await getDb();
  const rec = await db.get('meta', 'settings');
  const local = (rec?.value ?? {}) as Partial<SettingsDoc>;
  const remote = await graph.getJson<SettingsDoc>(SETTINGS_PATH);
  await graph.putJson(
    SETTINGS_PATH,
    {
      schemaVersion: SCHEMA_VERSION,
      updatedAt: nowIsoUtc(),
      defaultCategoryId: local.defaultCategoryId ?? remote?.data.defaultCategoryId ?? null,
      appVersion: APP_VERSION,
    },
    remote?.eTag ?? null,
  );
}

/** 一日のまとめ。1日1ファイルなので競合は日単位で局所化される。 */
async function pushDaily(op: OutboxOp, touched: Touched): Promise<void> {
  const db = await getDb();
  const date = op.dateKey!;
  const rec = await db.get('dailies', date);
  if (!rec) return;

  const month = rec.monthKey;
  const { monthKey: _m, searchText: _s, _dirty, _etag, ...cloud } = rec;
  void _m;
  void _s;
  void _dirty;
  const result = await graph.putJson(dailyPath(month, date), cloud, _etag);
  if (result !== 'conflict') {
    const fresh = await db.get('dailies', date);
    if (fresh) await db.put('dailies', { ...fresh, _dirty: false, _etag: result.eTag });
  }
  touched.add(month);
}

async function pushDailyDelete(op: OutboxOp, touched: Touched): Promise<void> {
  const month = op.month!;
  await graph.delete(dailyPath(month, op.dateKey!)).catch(() => undefined);
  touched.add(month);
}

/** ゴールは1ファイル。領域はid単位で統合し、セルは updatedAt の新しい方を採る。 */
async function pushGoals(): Promise<void> {
  const db = await getDb();
  const local = (await db.get('meta', 'goals'))?.value as GoalsDoc | undefined;
  if (!local) return;
  const remote = await graph.getJson<GoalsDoc>(GOALS_PATH);

  let merged: GoalsDoc = local;
  if (remote) {
    const areas = new Map(remote.data.areas.map((a) => [a.id, a]));
    for (const a of local.areas) areas.set(a.id, a);
    const cells = { ...remote.data.cells };
    for (const [k, v] of Object.entries(local.cells)) {
      const r = cells[k];
      if (!r || v.updatedAt >= r.updatedAt) cells[k] = v;
    }
    merged = { ...local, areas: [...areas.values()], cells };
    await db.put('meta', { key: 'goals', value: merged });
  }
  await graph.putJson(GOALS_PATH, merged, remote?.eTag ?? null);
}

async function pushReview(op: OutboxOp): Promise<void> {
  const db = await getDb();
  const rec = await db.get('reviews', op.week!);
  if (!rec) return;
  await graph.putJson(reviewPath(op.week!), rec);
}

/** タスク定義。id単位で統合し、同一idは新しい updatedAt を持つ側の内容を採る。 */
async function pushTasks(): Promise<void> {
  const db = await getDb();
  const local = (await db.get('meta', 'tasks'))?.value as TasksDoc | undefined;
  if (!local) return;
  const remote = await graph.getJson<TasksDoc>(TASKS_PATH);

  let merged: TasksDoc = local;
  if (remote) {
    const byId = new Map<string, TaskDef>(remote.data.tasks.map((t) => [t.id, t]));
    for (const t of local.tasks) byId.set(t.id, t);
    merged = { ...local, tasks: [...byId.values()] };
    await db.put('meta', { key: 'tasks', value: merged });
  }
  await graph.putJson(TASKS_PATH, merged, remote?.eTag ?? null);
}

/**
 * プロジェクト。id単位で統合し、同一idは updatedAt の新しい方を採る。
 * リモート側の一覧を土台にするため、削除は必ず論理削除（deletedAt）で表す。
 * 物理削除するとここで復活してしまう。
 */
async function pushProjects(): Promise<void> {
  const db = await getDb();
  const local = (await db.get('meta', 'projects'))?.value as ProjectsDoc | undefined;
  if (!local) return;
  const remote = await graph.getJson<ProjectsDoc>(PROJECTS_PATH);

  let merged: ProjectsDoc = local;
  if (remote) {
    const byId = new Map<string, Project>(remote.data.projects.map((p) => [p.id, p]));
    for (const p of local.projects) {
      const r = byId.get(p.id);
      byId.set(p.id, !r || p.updatedAt >= r.updatedAt ? p : r);
    }
    merged = { ...local, projects: [...byId.values()] };
    await db.put('meta', { key: 'projects', value: merged });
  }
  await graph.putJson(PROJECTS_PATH, merged, remote?.eTag ?? null);
}

function taskEntryPathOf(kind: 'daily' | 'weekly', period: string): string {
  return kind === 'daily' ? taskDailyPath(period.slice(0, 7), period) : taskWeeklyPath(period);
}

async function pushTaskEntry(op: OutboxOp, touched: Touched): Promise<void> {
  const db = await getDb();
  const kind = op.taskKind!;
  const period = op.period!;
  const rec = await db.get('taskEntries', kind === 'daily' ? `d:${period}` : `w:${period}`);
  if (!rec) return;

  const { key: _k, monthKey: _m, _dirty, _etag, ...cloud } = rec;
  void _k;
  void _m;
  void _dirty;
  const result = await graph.putJson(taskEntryPathOf(kind, period), cloud, _etag);
  if (result !== 'conflict') {
    const fresh = await db.get('taskEntries', rec.key);
    if (fresh) await db.put('taskEntries', { ...fresh, _dirty: false, _etag: result.eTag });
  }
  if (kind === 'daily') touched.add(period.slice(0, 7));
}

async function pushTaskEntryDelete(op: OutboxOp, touched: Touched): Promise<void> {
  const kind = op.taskKind!;
  const period = op.period!;
  await graph.delete(taskEntryPathOf(kind, period)).catch(() => undefined);
  if (kind === 'daily') touched.add(period.slice(0, 7));
}

async function bumpConflictCount(): Promise<void> {
  const db = await getDb();
  const cur = ((await db.get('meta', 'conflictCount'))?.value as number) ?? 0;
  await db.put('meta', { key: 'conflictCount', value: cur + 1 });
}

export async function runOp(op: OutboxOp, touched: Touched): Promise<void> {
  switch (op.type) {
    case 'entry.upsert':
      return pushEntry(op, touched);
    case 'entry.delete':
      return pushDelete(op, touched);
    case 'media.put':
      return uploadMedia(op.targetKey.replace(/^media:/, ''));
    case 'media.delete':
      return pushMediaDelete(op);
    case 'categories.put':
      return pushCategories();
    case 'settings.put':
      return pushSettings();
    case 'goals.put':
      return pushGoals();
    case 'daily.upsert':
      return pushDaily(op, touched);
    case 'daily.delete':
      return pushDailyDelete(op, touched);
    case 'review.put':
      return pushReview(op);
    case 'projects.put':
      return pushProjects();
    case 'tasks.put':
      return pushTasks();
    case 'taskentry.upsert':
      return pushTaskEntry(op, touched);
    case 'taskentry.delete':
      return pushTaskEntryDelete(op, touched);
  }
}

export async function finalizeIndexes(touched: Touched): Promise<void> {
  for (const month of touched) await rebuildIndex(month);
}
